import { useState, useEffect } from 'react';
import { isIOSDevice } from '../../../utils/deviceDetection';

interface SafeAreaInsets {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

/**
 * Custom hook for detecting safe area insets on iOS devices
 * Handles devices with notches, home indicators, etc.
 */
export const useSafeAreaInsets = () => {
  const [safeAreaInsets, setSafeAreaInsets] = useState<SafeAreaInsets>({
    top: 0,
    bottom: 0,
    left: 0,
    right: 0
  });
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    const getPlatformInfo = () => {
      try {
        const isiOSPlatform = isIOSDevice();
        setIsIOS(isiOSPlatform);

        if (isiOSPlatform && 'visualViewport' in window && window.visualViewport) {
          // Calculate safe area insets using CSS env() variables
          const computedStyle = getComputedStyle(document.documentElement);
          
          // Get the top inset with a fallback to standard iOS status bar height
          const envTopInset = parseInt(computedStyle.getPropertyValue('env(safe-area-inset-top)')) || 0;
          const topInset = Math.max(
            envTopInset,
            20 // Standard iOS status bar height (pre-iPhone X)
          );
          
          // For newer iPhones with notch, the status bar is typically 44-47px
          // This will be handled by the env() value if available
          const finalTopInset = envTopInset > 0 ? envTopInset : 
            (window.screen.height >= 812 ? 44 : 20); // iPhone X+ vs older models
          
          const bottomInset = Math.max(
            parseInt(computedStyle.getPropertyValue('env(safe-area-inset-bottom)')) || 0,
            34 // Default for iPhone X and newer with home indicator
          );
          const leftInset = parseInt(computedStyle.getPropertyValue('env(safe-area-inset-left)')) || 0;
          const rightInset = parseInt(computedStyle.getPropertyValue('env(safe-area-inset-right)')) || 0;

          setSafeAreaInsets({
            top: finalTopInset,
            bottom: bottomInset,
            left: leftInset,
            right: rightInset
          });
        } else {
          // Not iOS device, set all insets to 0
          setSafeAreaInsets({
            top: 0,
            bottom: 0,
            left: 0,
            right: 0
          });
        }
      } catch (_error) {
        // console.warn('Could not get platform info:', error);
      }
    };

    getPlatformInfo();
  }, []);

  return { safeAreaInsets, isIOS };
};